import { Skeleton, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import useIsMobile from '../../hooks/useIsMobile'
import { ChartSkeleton } from './ChartSkeleton'
import { formatDollarValue, formatTimestamp, useChartHeight } from './chartUtils'
import { commonTooltipProps, CustomTooltip } from './commonComponents'
import { PrecentChange } from './PrecentChange'
import { TotalChartsOverview, TotalVolume, loadChartsTotalVolume } from './chartsApi'
import BigNumber from 'bignumber.js'
import { getVolumeDataByWeek } from './processData'

export function VolumeChart(props: { className: string; overView: TotalChartsOverview }) {
    const { className = '', overView } = props
    const [volumeData, setVolumeData] = useState([] as TotalVolume[])
    const [weeklyVolumeData, setWeeklyVolumeData] = useState([] as TotalVolume[])
    const [timeframe, setTimeframe] = useState<'DAY' | 'WEEK'>('DAY')
    const [monthData, setMonthData] = useState(
        [] as {
            timestamp: number
            volume: number
        }[]
    )
    const chartHeight = useChartHeight()
    const isMobile = useIsMobile()
    const history = timeframe === 'DAY' ? monthData : weeklyVolumeData
    // const change = BigNumber(20)

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        if (volumeData) {
            filterMonthData()
        }
    }, [volumeData])

    async function loadData() {
        const response = await loadChartsTotalVolume()
        if (response) setVolumeData(response)

        if (response) setWeeklyVolumeData(getVolumeDataByWeek(response))
    }

    function filterMonthData() {
        if (!volumeData || volumeData.length === 0) return
        const currentDate = new Date()

        // Calculate the date 30 days ago
        const thirtyDaysAgo = new Date(currentDate)
        thirtyDaysAgo.setDate(currentDate.getDate() - 30)

        // Get the timestamp of the date 30 days ago
        const timestampThirtyDaysAgo = thirtyDaysAgo.getTime()

        const newData = [] as {
            timestamp: number
            volume: number
        }[]

        volumeData.map((item) => {
            if (item.timestamp * 1000 > timestampThirtyDaysAgo) {
                newData.push(item)
            }
        })
        setMonthData(newData)
    }

    function getCurrentVolume() {
        if (overView) return overView.volume_24h
        return 0
    }

    function renderTimeTablePicker() {
        if (!history) return null

        return (
            <div className='absolute top-[0px] right-[0px] flex rounded-xl border border-[#0C3F6D] p-1'>
                {(['DAY', 'WEEK'] as const).map((_timeframe) => (
                    <div key={_timeframe} onClick={() => setTimeframe(_timeframe)} className={`p-2 cursor-pointer ${_timeframe === timeframe ? 'bg-[#0C3F6D] rounded-[8px]' : ''}`}>
                        <Typography fontSize={!isMobile ? 16 : 14} fontWeight={500} lineHeight={1}>
                            {_timeframe === 'DAY' ? 'D' : 'W'}
                        </Typography>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div
            className={`h-full bg-[#0C1647] px-5 py-6 rounded-2xl ${className}`}
            style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
            <div className='relative flex w-full items-center justify-between'>
                <span className='flex text-white'>
                    Volume 24H
                    {overView && overView.volume_change_24h && (
                        <div className='ml-2 '>
                            <PrecentChange value={BigNumber(overView.volume_change_24h)} />
                        </div>
                    )}
                </span>
                {renderTimeTablePicker()}
            </div>
            <span className='text-[36px] font-bold text-white mb-5 block'>{getCurrentVolume() ? <>${formatDollarValue(getCurrentVolume()!)}</> : <Skeleton width={80} />}</span>

            {history ? (
                <ResponsiveContainer width={'100%'} height={chartHeight}>
                    <BarChart data={history} barCategoryGap={2}>
                        <CartesianGrid vertical={false} stroke='#0C3F6D' strokeDasharray='3' />
                        <XAxis
                            dataKey='timestamp'
                            height={!isMobile ? 20 : 15}
                            domain={[`dataMin`, 'dataMax']}
                            axisLine={{
                                stroke: '#549EFC',
                                strokeOpacity: 0.2,
                                strokeWidth: 1
                            }}
                            tick={{
                                fill: '#549EFC',
                                fontSize: !isMobile ? 14 : 12
                            }}
                            tickLine={{
                                stroke: '#549EFC',
                                strokeOpacity: 0.2,
                                strokeWidth: 1
                            }}
                            tickFormatter={formatTimestamp}
                            interval={'preserveStartEnd'}
                            minTickGap={timeframe === 'DAY' ? (!isMobile ? 40 : 20) : undefined}
                        />
                        <YAxis hide={true} padding={{ top: !isMobile ? 75 : 50 }} domain={[0, 'dataMax']} />
                        <Tooltip
                            cursor={{
                                fill: '#549EFC',
                                strokeWidth: 0,
                                fillOpacity: 0.8
                            }}
                            {...commonTooltipProps(isMobile)}
                            content={(props) => <CustomTooltip {...props} mode={timeframe} />}
                        />
                        <Bar type='linear' dataKey='volume' strokeWidth={2} stroke='#549EFC' fill='#549EFC' />
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <ChartSkeleton />
            )}
        </div>
    )
}
